import React, { memo } from 'react';

/**
 * Logo - the ONE way this app renders the LeadifyAI brand (icon, wordmark, lockups, motto).
 *
 * Every variant is an outlined SVG served from `public/brand/` (copied there from `brand/dist/web/app/` by
 * `cd brand/build && npm run sync:frontend`). The component renders a single `<img>`: no inline SVG, no
 * font, one cacheable request per file. Never re-type "LeadifyAI" or the motto in a font instead.
 *
 * ZERO LAYOUT SHIFT: the `width`/`height` attributes are computed from each file's viewBox (the table
 * below), so the box is reserved before the image arrives. The table is a constant, not a runtime read;
 * `__tests__/Logo.test.js` fails if it ever drifts from the files in `public/brand/`.
 *
 * ICON CUT: the brand rule (brand/tokens.json `usage.icon`) is "at 48 px and below use the heavier small
 * cut". `iconCut="auto"` applies it to the RENDERED height; `"icon"` / `"small"` force a cut. Lockups
 * carry their tile's cut inside the file, so `iconCut` only affects `variant="icon"`.
 */

/** viewBox [width, height] of every brand file in public/brand/ (light and dark twins share geometry). */
export const LOGO_VIEWBOXES = Object.freeze({
  'icon.svg': [1024, 1024],
  'icon-small.svg': [1024, 1024],
  'wordmark-on-light.svg': [659.15, 150.32],
  'wordmark-on-dark.svg': [659.15, 150.32],
  'lockup-horizontal-light.svg': [851.15, 159.5],
  'lockup-horizontal-dark.svg': [851.15, 159.5],
  'lockup-motto-light.svg': [896.15, 195],
  'lockup-motto-dark.svg': [896.15, 195],
  'motto-light.svg': [2312.47, 147.8],
  'motto-dark.svg': [2312.47, 147.8],
});

/** Which file each variant x tone renders. `icon` has no tone: the wash tile reads on both grounds. */
const FILES = Object.freeze({
  icon: { light: 'icon.svg', dark: 'icon.svg' },
  wordmark: { light: 'wordmark-on-light.svg', dark: 'wordmark-on-dark.svg' },
  lockup: { light: 'lockup-horizontal-light.svg', dark: 'lockup-horizontal-dark.svg' },
  lockupMotto: { light: 'lockup-motto-light.svg', dark: 'lockup-motto-dark.svg' },
  motto: { light: 'motto-light.svg', dark: 'motto-dark.svg' },
});

export const LOGO_VARIANTS = Object.freeze(Object.keys(FILES));

/** width / height per variant - for layout maths (e.g. "how wide is a 28 px lockup?" = 28 * LOGO_ASPECT.lockup). */
export const LOGO_ASPECT = Object.freeze(
  Object.fromEntries(
    LOGO_VARIANTS.map((v) => {
      const [w, h] = LOGO_VIEWBOXES[FILES[v].light];
      return [v, w / h];
    }),
  ),
);

/** At or below this rendered height the icon uses the heavier small cut (brand rule: "48 px and below"). */
export const SMALL_ICON_MAX_HEIGHT = 48;

const DEFAULT_VARIANT = 'lockup';
const DEFAULT_HEIGHT = 32;
const BRAND_NAME = 'LeadifyAI';
/** The approved motto (brand/tokens.json `motto`) - the text the motto image shows, so it is its alt. */
const MOTTO = 'Where Opportunities Find You';

/**
 * Resolve the file a variant/tone/height/cut renders. Unknown values fall back to the defaults
 * (lockup, light, auto) rather than rendering a broken image.
 */
export function logoFile(variant, tone = 'light', height = DEFAULT_HEIGHT, iconCut = 'auto') {
  const files = FILES[variant] || FILES[DEFAULT_VARIANT];
  const file = files[tone === 'dark' ? 'dark' : 'light'];
  if (file !== 'icon.svg') return file;
  if (iconCut === 'small') return 'icon-small.svg';
  if (iconCut === 'icon') return 'icon.svg';
  return height <= SMALL_ICON_MAX_HEIGHT ? 'icon-small.svg' : 'icon.svg';
}

/**
 * URL of a brand file in public/brand/ (honours PUBLIC_URL). Pass `base` (e.g. `window.location.href`) for an
 * ABSOLUTE url - needed where the consuming document may be about:blank (the analytics PDF's capture clone).
 */
export function logoSrc(file, base) {
  const rel = `${process.env.PUBLIC_URL || ''}/brand/${file}`;
  return base ? new URL(rel, base).href : rel;
}

/** Rendered width (integer px, as the HTML `width` attribute requires) for a file at a given height. */
export function logoWidth(file, height) {
  const [w, h] = LOGO_VIEWBOXES[file];
  return Math.round((height * w) / h);
}

const isValidHeight = (h) => typeof h === 'number' && Number.isFinite(h) && h > 0;

/**
 * @param {object} props
 * @param {'icon'|'wordmark'|'lockup'|'lockupMotto'|'motto'} [props.variant='lockup']
 * @param {'light'|'dark'} [props.tone='light'] - 'light' for light grounds, 'dark' for navy grounds
 * @param {number} props.height - rendered height in px (required; width is derived)
 * @param {'auto'|'icon'|'small'} [props.iconCut='auto'] - icon variant only
 * @param {boolean} [props.decorative=false] - alt="" + aria-hidden when a surrounding link/heading names it
 * @param {string} [props.className]
 * @param {object} [props.style] - plain style passthrough
 * Any other prop (id, data-*, ...) is passed to the <img>, but can never override src/alt/size.
 */
function Logo({
  variant = DEFAULT_VARIANT,
  tone = 'light',
  height,
  iconCut = 'auto',
  decorative = false,
  className,
  style,
  ...rest
}) {
  let h = height;
  if (!isValidHeight(h)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`Logo: "height" must be a positive number of px (got ${String(height)}); using ${DEFAULT_HEIGHT}.`);
    }
    h = DEFAULT_HEIGHT;
  }
  const file = logoFile(variant, tone, h, iconCut);
  const alt = decorative ? '' : variant === 'motto' ? MOTTO : BRAND_NAME;

  return (
    <img
      {...rest}
      src={logoSrc(file)}
      width={logoWidth(file, h)}
      height={Math.round(h)}
      alt={alt}
      aria-hidden={decorative ? 'true' : undefined}
      draggable={false}
      decoding="async"
      className={className}
      style={style}
    />
  );
}

export default memo(Logo);
